import { formatDate, formatPlanName, formatTimestamp } from './util'

export type StatusColorConfig = {
  label: string
  color: string
  bg: string
  darkColor: string
  darkBg: string
}

export const statusConfig: Record<string, StatusColorConfig> = {
  active: {
    label: 'Active',
    color: '#16a34a',
    bg: '#f0fdf4',
    darkColor: '#4ade80',
    darkBg: 'rgba(34, 197, 94, 0.12)',
  },
  inactive: {
    label: 'Inactive',
    color: '#64748b',
    bg: '#f1f5f9',
    darkColor: '#94a3b8',
    darkBg: 'rgba(148, 163, 184, 0.12)',
  },
  free: {
    label: 'Free',
    color: '#64748b',
    bg: '#f1f5f9',
    darkColor: '#94a3b8',
    darkBg: 'rgba(148, 163, 184, 0.12)',
  },
  trialing: {
    label: 'Trial',
    color: '#2563eb',
    bg: '#eff6ff',
    darkColor: '#60a5fa',
    darkBg: 'rgba(59, 130, 246, 0.12)',
  },
  past_due: {
    label: 'Past due',
    color: '#b45309',
    bg: '#fffbeb',
    darkColor: '#fbbf24',
    darkBg: 'rgba(245, 158, 11, 0.12)',
  },
  canceled: {
    label: 'Canceled',
    color: '#dc2626',
    bg: '#fef2f2',
    darkColor: '#f87171',
    darkBg: 'rgba(239, 68, 68, 0.12)',
  },
}

export const getStatusConfig = (status: string): StatusColorConfig => statusConfig[status] || statusConfig.inactive

type CompanyStatusFields = {
  status?: string
  cancelAt?: number | null
  canceledAt?: number | null
  canceledBy?: string
  isUpgraded?: boolean
}

// A new signup is stored as status 'canceled' (schema default) and was never paid.
// canceledAt (unix seconds) is written only when a paid plan actually ends, so
// canceled without it reads as Free - including paid plans that ended before the
// date was recorded (owner decision).
export const getCompanyStatusKey = (company?: CompanyStatusFields): string => {
  const status = company?.status || ''
  if (status === 'canceled' && !company?.canceledAt) return 'free'
  return status
}

const isCancelScheduled = (company: CompanyStatusFields, nowSec: number): boolean =>
  !!company.canceledBy && !company.isUpgraded && !!company.cancelAt && company.cancelAt > nowSec

export const getCompanyStatusConfig = (company?: CompanyStatusFields): StatusColorConfig => {
  const key = getCompanyStatusKey(company)
  const config = getStatusConfig(key)
  if (key === 'canceled' && company?.canceledAt) {
    return { ...config, label: `Canceled ${formatTimestamp(company.canceledAt)}` }
  }
  // An owner-scheduled cancel carries canceledBy. A plan change also sets a future cancelAt
  // (and isUpgraded), and canceledBy can be left over from an earlier cancel, so skip upgrades.
  if (company && company.status !== 'canceled' && isCancelScheduled(company, Date.now() / 1000)) {
    return { ...config, label: `Cancels ${formatTimestamp(company.cancelAt)}` }
  }
  return config
}

type SubscriberListFields = CompanyStatusFields & {
  planType?: string
  subscriptionEndDate?: number | null
  gracePeriodEndsAt?: number | null
  trialUsedAt?: string | null
  // Added by the subscription-metrics work; blank lines until the API sends them.
  paidSince?: number | null
  trialOutcome?: 'converted' | 'canceled' | null
  trialEndedAt?: number | null
}

export type SubscriberLineTone = 'muted' | 'warn'

export type SubscriberListCells = {
  plan: StatusColorConfig
  planLine: string
  status: StatusColorConfig
  statusLine: string
  statusLineTone: SubscriberLineTone
}

const DAY = 86400
const TRIAL_WARN_DAYS = 7

const daysLeft = (endSec: number, nowSec: number): string => {
  const days = Math.ceil((endSec - nowSec) / DAY)
  if (days <= 0) return 'Ends today'
  return days === 1 ? '1 day left' : `${days} days left`
}

const monthsPaid = (fromSec: number, toSec: number): string => {
  const months = Math.max(1, Math.round((toSec - fromSec) / (30.44 * DAY)))
  return months === 1 ? 'Paid 1 month' : `Paid ${months} months`
}

/**
 * Subscribers list cells. Plan line = when paying started or will start; Status line = what
 * happens next. A trial that ran out and an owner-canceled trial are both "Trial canceled".
 */
export const getSubscriberListCells = (
  company: SubscriberListFields,
  nowSec: number = Date.now() / 1000
): SubscriberListCells => {
  const plan = { label: formatPlanName(company.planType || 'free'), ...getPlanColor(company.planType) }
  const paidSinceLine = company.paidSince ? `Paid since ${formatTimestamp(company.paidSince)}` : ''
  const cells = (
    status: StatusColorConfig,
    planLine = '',
    statusLine = '',
    statusLineTone: SubscriberLineTone = 'muted'
  ): SubscriberListCells => ({ plan, planLine, status, statusLine, statusLineTone })
  const key = getCompanyStatusKey(company)
  const canceling = isCancelScheduled(company, nowSec)

  if (key === 'trialing') {
    if (canceling) {
      return cells(
        { ...statusConfig.canceled, label: 'Trial canceled' },
        `Downgrades ${formatTimestamp(company.cancelAt)}`,
        daysLeft(company.cancelAt, nowSec)
      )
    }
    const end = company.subscriptionEndDate
    if (!end) return cells(statusConfig.trialing)
    const soon = Math.ceil((end - nowSec) / DAY) <= TRIAL_WARN_DAYS
    return cells(
      statusConfig.trialing,
      `Paid from ${formatTimestamp(end)}`,
      daysLeft(end, nowSec),
      soon ? 'warn' : 'muted'
    )
  }
  if (key === 'active') {
    if (canceling) {
      return cells(
        { ...statusConfig.canceled, label: 'Canceling' },
        paidSinceLine,
        `Downgrades ${formatTimestamp(company.cancelAt)}`
      )
    }
    const renews = company.subscriptionEndDate ? `Renews ${formatTimestamp(company.subscriptionEndDate)}` : ''
    return cells(statusConfig.active, paidSinceLine, renews)
  }
  if (key === 'past_due') {
    const payBy = company.gracePeriodEndsAt ? `Pay by ${formatTimestamp(company.gracePeriodEndsAt)}` : ''
    return cells(statusConfig.past_due, paidSinceLine, payBy, 'warn')
  }
  if (key === 'canceled' && company.canceledAt) {
    const downgraded = `Downgraded ${formatTimestamp(company.canceledAt)}`
    if (company.trialOutcome === 'canceled' && !company.paidSince) {
      const trial = company.trialUsedAt
        ? `Trial ${formatDate(company.trialUsedAt)} - ${formatTimestamp(company.trialEndedAt || company.canceledAt)}`
        : ''
      return cells({ ...statusConfig.canceled, label: 'Trial canceled' }, trial, downgraded)
    }
    const paid = company.paidSince ? monthsPaid(company.paidSince, company.canceledAt) : ''
    return cells(statusConfig.canceled, paid, downgraded)
  }
  return cells(getStatusConfig(key))
}

/**
 * Admin-account lifecycle states, shown as a Status chip in the admin list.
 * A soft-deleted row used to be signalled by a red row tint ALONE — colour
 * only, invisible to a screen reader. The chip is now the real signal and the
 * tint is decoration.
 */
export const adminAccountStatusConfig: Record<string, StatusColorConfig> = {
  active: statusConfig.active,
  invited: {
    label: 'Invited',
    color: '#b45309',
    bg: '#fffbeb',
    darkColor: '#fbbf24',
    darkBg: 'rgba(245, 158, 11, 0.12)',
  },
  deleted: {
    label: 'Deleted',
    color: '#b91c1c',
    bg: '#fef2f2',
    darkColor: '#f87171',
    darkBg: 'rgba(239, 68, 68, 0.12)',
  },
}

export const getAdminAccountStatusConfig = (status: string): StatusColorConfig =>
  adminAccountStatusConfig[status] || adminAccountStatusConfig.active

/**
 * Admin role chips. The Role column used to be a constant — every row read
 * "admin" because SuperAdmin rows were filtered out of the list entirely.
 */
export const adminRoleConfig: Record<string, StatusColorConfig> = {
  superadmin: {
    label: 'Super Admin',
    color: '#6d28d9',
    bg: '#f5f3ff',
    darkColor: '#a78bfa',
    darkBg: 'rgba(139, 92, 246, 0.12)',
  },
  admin: {
    label: 'Admin',
    color: '#1d4ed8',
    bg: '#eff6ff',
    darkColor: '#60a5fa',
    darkBg: 'rgba(59, 130, 246, 0.12)',
  },
}

export const getAdminRoleConfig = (role: string): StatusColorConfig =>
  adminRoleConfig[(role || '').toLowerCase()] || adminRoleConfig.admin

export const planColors: Record<string, { color: string; bg: string; darkColor: string; darkBg: string }> = {
  free: { color: '#64748b', bg: '#f1f5f9', darkColor: '#94a3b8', darkBg: 'rgba(148, 163, 184, 0.12)' },
  pro: { color: '#7c3aed', bg: '#f5f3ff', darkColor: '#a78bfa', darkBg: 'rgba(139, 92, 246, 0.12)' },
  proplus: { color: '#b45309', bg: '#fffbeb', darkColor: '#fbbf24', darkBg: 'rgba(245, 158, 11, 0.12)' },
  starter: { color: '#0891b2', bg: '#ecfeff', darkColor: '#22d3ee', darkBg: 'rgba(6, 182, 212, 0.12)' },
  basic: { color: '#2563eb', bg: '#eff6ff', darkColor: '#60a5fa', darkBg: 'rgba(59, 130, 246, 0.12)' },
  enterprise: { color: '#059669', bg: '#ecfdf5', darkColor: '#34d399', darkBg: 'rgba(52, 211, 153, 0.12)' },
}

export const defaultPlanColor = {
  color: '#64748b',
  bg: '#f1f5f9',
  darkColor: '#94a3b8',
  darkBg: 'rgba(148, 163, 184, 0.12)',
}

export const getPlanColor = (planType: string) => {
  if (!planType) return defaultPlanColor
  return planColors[planType.toLowerCase()] || defaultPlanColor
}

export type TicketStatusConfig = {
  label: string
  color: string
  darkColor: string
}

export const ticketStatusConfig: Record<number, TicketStatusConfig> = {
  1: { label: 'Open', color: '#16a34a', darkColor: '#4ade80' },
  2: { label: 'Replied', color: '#2563eb', darkColor: '#60a5fa' },
  3: { label: 'Closed', color: '#dc2626', darkColor: '#f87171' },
}

export const defaultTicketStatus: TicketStatusConfig = {
  label: 'Unknown',
  color: '#64748b',
  darkColor: '#94a3b8',
}

export const getTicketStatusConfig = (status: number): TicketStatusConfig =>
  ticketStatusConfig[status] || defaultTicketStatus

/**
 * Subscriber E-signature tab: the provider a kind is on and the state of each readiness check.
 * Reuses the palette above - BoldSign is the neutral default, In-house the blue "configured"
 * tone, and "Not checked" the amber of an unfinished state, never the green of a pass.
 */
export const signingChipConfig: Record<'boldsign' | 'inhouse' | 'pass' | 'fail' | 'notChecked', StatusColorConfig> = {
  boldsign: { ...statusConfig.inactive, label: 'BoldSign' },
  inhouse: { ...adminRoleConfig.admin, label: 'In-house' },
  pass: { ...statusConfig.active, label: 'Pass' },
  fail: { ...statusConfig.canceled, label: 'Fail' },
  notChecked: { ...adminAccountStatusConfig.invited, label: 'Not checked' },
}
